import _ from 'lodash';
import { EMITTER_CONSTANTS } from '@/shared/config';
import emitter from '@/shared/lib/eventEmitter';

// The 4 batch queues moved verbatim from the socket god-class (class fields become module-level
// state, bodies unchanged including the console.log lines). No reset is added here — nothing
// cancels these timers today, so adding one now would be a behavior change (see the plan's queue
// table). `addQueueRoomUsers` is a new name for what was inline logic inside `roomUsersListener` —
// it needed its own function so its module-level `let`s can be reassigned from within this module
// (an ES module import binding is read-only outside its own file); the logic is unchanged.

const queueECGEvent: string[] = [];
const queueNewEvent: any[] = [];
let timeOutEventUpdate: ReturnType<typeof setTimeout> | undefined;

const queueReportUpdate: any[] = [];
let timeOutReportUpdate: ReturnType<typeof setTimeout> | undefined;

// Referenced but never assigned anywhere in the codebase (grepped repo-wide) — the
// `if (updateReportReminder)` guard in addQueueHandleUpdateReport is therefore always false.
// Pre-existing dead code, carried across as-is.
export let updateReportReminder: ((msgs: any[]) => void) | undefined;

const queueOverviewUpdate: any[] = [];
let timeOutOverviewtUpdate: ReturnType<typeof setTimeout> | undefined;

const roomUsersEvent: any[] = [];
const roomUsersReport: any[] = [];
let timeOutRoomEvent: ReturnType<typeof setTimeout> | undefined;

/**
 * Batches ECG event refreshes so a burst of socket updates costs the UI a single reload. Pushes the study id, plus the message's id and stripImgs when the message is non-empty, onto two queues drained by one five-second timer that emits UPDATE_TOTAL_EVENTS and, when populated, UPDATE_NEW_EVENT_ECG.
 *
 * @param studyId - Study whose event totals need refreshing.
 * @param msg - Optional new-event payload contributing its id and stripImgs to the second queue.
 * @returns Nothing.
 */
export const addQueueECGEventUpdate = (studyId: string, msg: any = {}) => {
  queueECGEvent.push(studyId);
  if (!_.isEmpty(msg)) {
    queueNewEvent.push({ id: msg.id, stripImgs: msg.stripImgs });
  }
  if (!timeOutEventUpdate) {
    timeOutEventUpdate = setTimeout(() => {
      timeOutEventUpdate = undefined;
      emitter.emit(
        EMITTER_CONSTANTS.UPDATE_TOTAL_EVENTS,
        queueECGEvent.splice(0, queueECGEvent.length),
      );
      if (queueNewEvent.length > 0) {
        emitter.emit(
          EMITTER_CONSTANTS.UPDATE_NEW_EVENT_ECG,
          queueNewEvent.splice(0, queueNewEvent.length),
        );
      }
    }, 5000);
  }
};

/**
 * Batches report-reminder refreshes for call-center users. Pushes the message onto the report queue and, on the first push, arms a one-second timer that drains it into the registered updateReportReminder callback when one has been set.
 *
 * @param msg - Report update entry carrying its type and the socket data.
 * @returns Nothing.
 */
export const addQueueHandleUpdateReport = (msg: any) => {
  queueReportUpdate.push(msg);
  if (!timeOutReportUpdate) {
    timeOutReportUpdate = setTimeout(() => {
      timeOutReportUpdate = undefined;
      if (updateReportReminder) {
        console.log('this.queueReportUpdate', queueReportUpdate);
        updateReportReminder(queueReportUpdate.splice(0, queueReportUpdate.length));
      }
    }, 1000);
  }
};

/**
 * Batches call-center overview refreshes triggered by report changes. Pushes the message onto the overview queue and, on the first push, arms a fifteen-second timer that drains the queue into one UPDATE_OVERVIEW emit.
 *
 * @param msg - Overview update entry carrying its type and the socket data.
 * @returns Nothing.
 */
export const addQueueHandleUpdateOverview = (msg: any) => {
  queueOverviewUpdate.push(msg);
  if (!timeOutOverviewtUpdate) {
    timeOutOverviewtUpdate = setTimeout(() => {
      timeOutOverviewtUpdate = undefined;
      emitter.emit(
        EMITTER_CONSTANTS.UPDATE_OVERVIEW,
        queueOverviewUpdate.splice(0, queueOverviewUpdate.length),
      );
    }, 15000);
  }
};

/**
 * Batches room presence so the event and report viewer lists refresh at most once a second. Routes the message into the Event or Report bucket by its roomType and arms one shared timer that drains each non-empty bucket into EVENT_ROOM_USER or REPORT_ROOM_USER.
 *
 * @param msg - Presence payload carrying the roomType and the viewers in that room.
 * @returns Nothing.
 */
export const addQueueRoomUsers = (msg: any) => {
  if (msg.roomType === 'Event') {
    roomUsersEvent.push(msg);
  } else if (msg.roomType === 'Report') {
    roomUsersReport.push(msg);
  }
  if (!timeOutRoomEvent) {
    timeOutRoomEvent = setTimeout(() => {
      if (roomUsersEvent.length > 0) {
        emitter.emit(
          EMITTER_CONSTANTS.EVENT_ROOM_USER,
          roomUsersEvent.splice(0, roomUsersEvent.length),
        );
      }
      if (roomUsersReport.length > 0) {
        emitter.emit(
          EMITTER_CONSTANTS.REPORT_ROOM_USER,
          roomUsersReport.splice(0, roomUsersReport.length),
        );
      }
      timeOutRoomEvent = undefined;
    }, 1000);
  }
};
